import { useState, useRef, useCallback, useEffect } from 'react';
import { container } from 'tsyringe';
import { RentalServiceEvent } from '../../../../../domain/events/rentalServicesEvent';
import { BabySeatOption } from '../../../../../domain/model/availability/offersData';
import { getCookieShopify } from '../../../../../application/services/cookieService';
const DEBOUNCE_MS = 2000; // 2-second debounce per AC#5/AC#6
const GLOBAL_MAX_SEATS = 3;
/**
 * Custom hook that manages the Momento 2 baby seat quotation flow.
 *
 * - Debounces quantity changes for 2 seconds (AC#5, AC#6)
 * - Exposes isPendingQuotation (immediate, hides stale prices) and
 *   isQuoting (after debounce, shows spinner/dimming)
 * - Listens for WebSocket quotation results
 * - Enforces global max of 3 seats across all types
 */
export function useBabySeatQuotation(options) {
    const { offerId, transactionId, idSession, rateType } = options;
    const [isPendingQuotation, setIsPendingQuotation] = useState(false);
    const [isQuoting, setIsQuoting] = useState(false);
    // Dedicated flag for the rate-type toggle visual feedback. Decoupled from the WS-driven
    // flags so a SearchSingleResults / BabySeatQuotationResults message arriving mid-animation
    // (common after a language change, which fires resetQuotation) cannot end the spinner early.
    const [isRateTypeAnimating, setIsRateTypeAnimating] = useState(false);
    const [quotedOptions, setQuotedOptions] = useState(null);
    const [quotedExtraChargeDetail, setQuotedExtraChargeDetail] = useState(null);
    // Seat types whose count diverges from the last value confirmed by the backend.
    // Only these seats should blink; untouched seats keep a stable visual state.
    const [loadingSeatTypes, setLoadingSeatTypes] = useState(() => new Set());
    const debounceTimerRef = useRef(null);
    const listenerRegisteredRef = useRef(false);
    const isQuotingRef = useRef(false);
    // Pending debounced selection that has not been emitted yet. Used by the
    // page-unload flush below so a refresh fired during the 2-second debounce
    // window can still push *reducing* changes (commonly: the user removed
    // every seat before refreshing) through to the backend.
    const pendingPayloadRef = useRef(null);
    // Last selection this client explicitly committed to the backend via a
    // debounced send or `requestAvailabilityRefresh`. Used by the flush to
    // decide whether the pending change is a reduction (safe to flush — the
    // backend already associates us with equal-or-greater seats) or an
    // increase (unsafe to flush — persisting adds the user never intended to
    // confirm would leave phantom taxes on the next availability response).
    // Initialized to {0,0,0}: a fresh client has not told the backend about
    // any seats yet.
    const lastConfirmedPayloadRef = useRef({
        infantSeatCount: 0,
        childSeatCount: 0,
        boosterSeatCount: 0,
    });
    // Track the latest IDs so the flush handler can build a payload from its
    // useEffect-scoped closure even when props changed after mount.
    const offerIdRef = useRef(offerId);
    const transactionIdRef = useRef(transactionId);
    const idSessionRef = useRef(idSession);
    useEffect(() => { offerIdRef.current = offerId; }, [offerId]);
    useEffect(() => { transactionIdRef.current = transactionId; }, [transactionId]);
    useEffect(() => { idSessionRef.current = idSession; }, [idSession]);
    // Keep ref in sync with state so event listeners read the current value
    useEffect(() => {
        isQuotingRef.current = isQuoting;
    }, [isQuoting]);
    // Register WebSocket result listeners once
    useEffect(() => {
        if (listenerRegisteredRef.current)
            return;
        listenerRegisteredRef.current = true;
        try {
            const application = container.resolve('IRegisterEventServiceBus');
            // Listen for BabySeatQuotationResults (direct quotation response)
            application.handle(RentalServiceEvent.BABY_SEAT_QUOTATION_RESULT, (payload) => {
                var _a, _b;
                if (!payload)
                    return;
                // Ignore stale responses that arrive while a debounce is active —
                // the pending timer will fire a new request with the latest counts.
                if (debounceTimerRef.current)
                    return;
                const result = {
                    babySeatOptions: ((_a = payload.babySeatOptions) !== null && _a !== void 0 ? _a : []).filter((o) => o.isAvailable === true).map((o) => new BabySeatOption(o)),
                    isQuotation: (_b = payload.isQuotation) !== null && _b !== void 0 ? _b : true,
                };
                // Replace quoted options with the new result set from backend.
                setQuotedOptions(result.babySeatOptions);
                // FX_7661: capture the backend-refreshed ExtraChargeDetail for this baby seat
                // combination. The provider (e.g. Hertz) returns destination taxes only when a
                // quotation is requested — not in the original availability search — so without
                // this the frontend never learns the real pay-at-destination taxes and the
                // "Taxes and fees" row stays hidden on the confirmation card even though the
                // email shows the correct value. Example payload seen in stage for reservation
                // #1729: updatedExtraChargeDetail = [{type:1, amount:0}, {type:2, amount:5.29}].
                // When the provider returns no tax info for the current combination (null or
                // missing), we keep the previous value instead of clobbering it — that way a
                // transient empty response does not hide a row the user already saw.
                if (Array.isArray(payload.updatedExtraChargeDetail)) {
                    setQuotedExtraChargeDetail(payload.updatedExtraChargeDetail);
                }
                setIsQuoting(false);
                setIsPendingQuotation(false);
                setLoadingSeatTypes(new Set());
            });
            // Listen for SearchSingleResults — after a baby seat quotation the backend
            // re-executes SearchSingle to deliver recalculated taxes and totals.
            // When this arrives while a quotation is in flight, clear the spinner,
            // update baby seat options from the complete offer response, and adopt
            // the recalculated extraChargeDetail so the UI's "Taxes and Fees" row
            // reflects the current selection.
            application.handle(RentalServiceEvent.AVAILABILITY_SEARCH_SINGLE_RESULT, (payload) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                if (!payload)
                    return;
                if (!isQuotingRef.current)
                    return;
                if (debounceTimerRef.current)
                    return;
                // Reject messages for a different offer than the one the user is on.
                // The backend may republish SearchSingle for the sibling offer (basic ↔
                // all-inclusive pair) when its baby seats finish validating; that update
                // belongs to the other tariff's state and must not clear the quoting
                // spinner here.
                const incomingId = (_b = (_a = payload === null || payload === void 0 ? void 0 : payload.offer) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : payload === null || payload === void 0 ? void 0 : payload.id;
                if (incomingId && offerIdRef.current && incomingId !== offerIdRef.current)
                    return;
                const offer = (_c = payload.offer) !== null && _c !== void 0 ? _c : payload;
                const rawOptions = (_g = (_f = (_e = (_d = offer === null || offer === void 0 ? void 0 : offer.babySeatOptions) !== null && _d !== void 0 ? _d : offer === null || offer === void 0 ? void 0 : offer.babySeatAddOns) !== null && _e !== void 0 ? _e : offer === null || offer === void 0 ? void 0 : offer.babySeatAddons) !== null && _f !== void 0 ? _f : offer === null || offer === void 0 ? void 0 : offer.babySeats) !== null && _g !== void 0 ? _g : [];
                const options = rawOptions.filter((o) => o.isAvailable === true).map((o) => new BabySeatOption(o));
                if (options.length > 0) {
                    setQuotedOptions(options);
                }
                // Adopt the recalculated extraChargeDetail from the offer so the
                // consumer ("Taxes and Fees" row in the price breakdown) reflects the
                // taxes for the current selection instead of the stale values frozen
                // in myData at initial load. Without this, a session that carries
                // contaminated extras in its initial availability response (for
                // example because a prior client sent a stale quotation.request)
                // would keep displaying the phantom taxes for the entire session,
                // even after the user removes all seats — the exact symptom reported.
                const recalculatedExtras = (_l = (_k = (_j = (_h = offer === null || offer === void 0 ? void 0 : offer.pricesDetail) === null || _h === void 0 ? void 0 : _h.currency) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k.total) === null || _l === void 0 ? void 0 : _l.extraChargeDetail;
                if (Array.isArray(recalculatedExtras)) {
                    setQuotedExtraChargeDetail(recalculatedExtras.map((d) => { var _a, _b; return ({ type: Number((_a = d === null || d === void 0 ? void 0 : d.type) !== null && _a !== void 0 ? _a : 0), amount: Number((_b = d === null || d === void 0 ? void 0 : d.amount) !== null && _b !== void 0 ? _b : 0) }); }));
                }
                setIsQuoting(false);
                setIsPendingQuotation(false);
                setLoadingSeatTypes(new Set());
            });
        }
        catch (_a) {
            // Container may not be ready during tests or SSR; degrade gracefully
        }
    }, []);
    // Reset quoted options when offer changes
    useEffect(() => {
        setQuotedOptions(null);
        setQuotedExtraChargeDetail(null);
        setLoadingSeatTypes(new Set());
    }, [offerId]);
    // Reset quoted options when rateType changes (e.g. toggle between Basic and All Inclusive).
    //
    // The toggle handler (driverFormCard.handleSwitchChange) calls markAsQuoting() — which
    // sets isQuoting/isPendingQuotation true — before changing rateType. We mirror that here
    // for symmetry and turn all three flags off after the timeout. This is the safety net
    // for the case where no WS message arrives (no sibling offer to switch to, provider
    // timeout, etc.) — without it, markAsQuoting would leave the flags stuck on true.
    //
    // isRateTypeAnimating is dedicated to the toggle visual feedback so a SearchSingle or
    // BabySeatQuotation result that lands inside the 400 ms window (common after a language
    // change, where resetQuotation fires automatically) cannot end the spinner early via the
    // listeners that flip isQuoting/isPendingQuotation off.
    //
    // loadingSeatTypes is filled with all three seat type ids so each baby-seat__item gets
    // the --loading modifier and the per-seat pulse animation runs alongside the price block
    // pulse — without it, the only visible feedback was on the bottom price card, which is
    // typically out of the user's viewport while they interact with the toggle.
    useEffect(() => {
        if (rateType === undefined)
            return;
        setQuotedOptions(null);
        setQuotedExtraChargeDetail(null);
        setLoadingSeatTypes(new Set([0, 1, 2]));
        setIsQuoting(true);
        setIsPendingQuotation(true);
        setIsRateTypeAnimating(true);
        const t = setTimeout(() => {
            setIsQuoting(false);
            setIsPendingQuotation(false);
            setIsRateTypeAnimating(false);
            setLoadingSeatTypes(new Set());
        }, 400);
        return () => clearTimeout(t);
    }, [rateType]);
    const requestQuotation = useCallback((infantCount, childCount, boosterCount) => {
        // Enforce global max
        const total = infantCount + childCount + boosterCount;
        if (total > GLOBAL_MAX_SEATS)
            return;
        // Mark as pending IMMEDIATELY so the UI never renders stale prices.
        // This does NOT trigger the loading/dimming visual — only isQuoting does.
        setIsPendingQuotation(true);
        // Reconcile per-seat loading: any seat whose count differs from the last
        // backend-confirmed value is marked as loading. Clicks that cancel out
        // (e.g. +Infant then -Infant before debounce fires) leave the set empty.
        const confirmed = lastConfirmedPayloadRef.current;
        const nextLoading = new Set();
        if (infantCount !== confirmed.infantSeatCount)
            nextLoading.add(0);
        if (childCount !== confirmed.childSeatCount)
            nextLoading.add(1);
        if (boosterCount !== confirmed.boosterSeatCount)
            nextLoading.add(2);
        setLoadingSeatTypes(nextLoading);
        // Clear any existing debounce timer — every +/- click restarts the window
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // Record the pending selection so a page-unload flush can look at it.
        // Only reductions (pending.total <= lastConfirmed.total) will actually
        // be flushed — increases would persist adds the user abandoned.
        pendingPayloadRef.current = {
            infantSeatCount: infantCount,
            childSeatCount: childCount,
            boosterSeatCount: boosterCount,
        };
        // Debounce: wait 2 seconds before sending (AC#5/AC#6).
        // All quantity changes go through the debounce, including going back to 0,
        // because the provider must recalculate taxes/totals without seats.
        debounceTimerRef.current = setTimeout(() => {
            // Mark debounce as complete so the WebSocket listener accepts
            // the response for this request instead of discarding it.
            debounceTimerRef.current = null;
            // NOW activate loading visual (spinner, dimming) — request is being sent
            setIsQuoting(true);
            try {
                const eventBusSocket = container.resolve('IEventBusSocket');
                const quotationPayload = {
                    offerId,
                    transactionId,
                    idSession,
                    selections: {
                        infantSeatCount: infantCount,
                        childSeatCount: childCount,
                        boosterSeatCount: boosterCount,
                    },
                    isQuotation: true,
                };
                const message = {
                    event: RentalServiceEvent.BABY_SEAT_QUOTATION_REQUEST,
                    data: JSON.stringify({
                        ...quotationPayload,
                        sessionUserId: getCookieShopify(),
                    }),
                    isCache: false,
                    ip: '',
                };
                eventBusSocket.send(message);
                // Backend now associates this session with these counts.
                lastConfirmedPayloadRef.current = {
                    infantSeatCount: infantCount,
                    childSeatCount: childCount,
                    boosterSeatCount: boosterCount,
                };
                pendingPayloadRef.current = null;
            }
            catch (_a) {
                // If WebSocket is unavailable, stop loading
                setIsQuoting(false);
                setIsPendingQuotation(false);
                setLoadingSeatTypes(new Set());
            }
        }, DEBOUNCE_MS);
    }, [offerId, transactionId, idSession]);
    const resetQuotation = () => {
        // Cancel any pending debounce so it does not fire after the reset
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        setQuotedOptions(null);
        setQuotedExtraChargeDetail(null);
        // resetQuotation sends `selections: null` which asks the backend for
        // the last-known state; it does not commit anything, so the pending
        // debounced payload (if any) is moot but no longer recoverable.
        pendingPayloadRef.current = null;
        // Set loading state immediately for spinner
        setIsQuoting(true);
        setIsPendingQuotation(true);
        try {
            const eventBusSocket = container.resolve('IEventBusSocket');
            const quotationPayload = {
                offerId,
                transactionId,
                idSession,
                selections: null,
                isQuotation: true,
            };
            const message = {
                event: RentalServiceEvent.BABY_SEAT_QUOTATION_REQUEST,
                data: JSON.stringify({
                    ...quotationPayload,
                    sessionUserId: getCookieShopify(),
                }),
                isCache: false,
                ip: '',
            };
            eventBusSocket.send(message);
        }
        catch (_a) {
            // If WebSocket is unavailable, stop loading
            setIsQuoting(false);
            setIsPendingQuotation(false);
            setLoadingSeatTypes(new Set());
        }
    };
    /**
     * Set loading states without sending a WebSocket request.
     * Use in handleSwitchChange to prevent the baby seat section from
     * disappearing during offer transitions. The actual request is sent
     * by the useEffect on formData.offerId via resetQuotation().
     */
    const markAsQuoting = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        // The caller is about to fire a fresh request that supersedes any
        // pending debounced selection.
        pendingPayloadRef.current = null;
        setIsQuoting(true);
        setIsPendingQuotation(true);
    }, []);
    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    // Flush a pending *reducing* quotation synchronously on page unload.
    //
    // The 2-second debounce that normally batches +/- clicks becomes a
    // correctness hole if the user refreshes during that window: the browser
    // cancels the setTimeout before it fires, the backend never learns about
    // the change, and its session-scoped quotation cache keeps the previous
    // selection. On the next availability response the cached
    // extraChargeDetail comes back and the UI paints phantom taxes even
    // though the counters show 0.
    //
    // Flushing unconditionally is not safe either: a user who clicks
    // +Infant twice and immediately refreshes has no intent to pay for those
    // seats — they are effectively "undoing" the click by refreshing. An
    // indiscriminate flush would commit {2,0,0} to the backend cache and the
    // next reload would surface those taxes.
    //
    // The compromise is to flush *only* when the pending selection has a
    // lower-or-equal total than the last selection this client confirmed to
    // the backend. That keeps "remove before refresh" working (pending
    // reduces from a confirmed state) without persisting "add before refresh"
    // increases the user never committed.
    useEffect(() => {
        const flushPendingQuotation = () => {
            const pending = pendingPayloadRef.current;
            if (!pending)
                return;
            const confirmed = lastConfirmedPayloadRef.current;
            const pendingTotal = pending.infantSeatCount + pending.childSeatCount + pending.boosterSeatCount;
            const confirmedTotal = confirmed.infantSeatCount + confirmed.childSeatCount + confirmed.boosterSeatCount;
            // Only flush when the user is reducing or re-shaping a state the
            // backend already has. Increases over the confirmed total get
            // discarded with the debounce timer.
            if (pendingTotal > confirmedTotal) {
                pendingPayloadRef.current = null;
                return;
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            try {
                const eventBusSocket = container.resolve('IEventBusSocket');
                const quotationPayload = {
                    offerId: offerIdRef.current,
                    transactionId: transactionIdRef.current,
                    idSession: idSessionRef.current,
                    selections: pending,
                    isQuotation: true,
                };
                const message = {
                    event: RentalServiceEvent.BABY_SEAT_QUOTATION_REQUEST,
                    data: JSON.stringify({
                        ...quotationPayload,
                        sessionUserId: getCookieShopify(),
                    }),
                    isCache: false,
                    ip: '',
                };
                eventBusSocket.send(message);
                lastConfirmedPayloadRef.current = pending;
            }
            catch (_a) {
                // Socket may already be closing — nothing more we can do.
            }
            finally {
                pendingPayloadRef.current = null;
            }
        };
        window.addEventListener('beforeunload', flushPendingQuotation);
        // pagehide is more reliable than beforeunload on Safari and mobile
        // browsers for the refresh/back-button path.
        window.addEventListener('pagehide', flushPendingQuotation);
        return () => {
            window.removeEventListener('beforeunload', flushPendingQuotation);
            window.removeEventListener('pagehide', flushPendingQuotation);
        };
    }, []);
    /**
     * Fires an immediate (non-debounced) WebSocket availability request.
     * Used when the coverage toggle changes — the new rate requires a fresh
     * baby seat query with no delay and no caching (isCache: false).
     */
    const requestAvailabilityRefresh = useCallback((infantCount, childCount, boosterCount) => {
        // Set loading state immediately so the baby seat section stays visible
        setIsQuoting(true);
        setIsPendingQuotation(true);
        // Cancel any pending debounce so we do not fire a stale request afterward
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        // This immediate send supersedes any debounced selection still queued.
        pendingPayloadRef.current = null;
        // Fire immediately (no debounce delay)
        try {
            const eventBusSocket = container.resolve('IEventBusSocket');
            const quotationPayload = {
                offerId,
                transactionId,
                idSession,
                selections: {
                    infantSeatCount: infantCount,
                    childSeatCount: childCount,
                    boosterSeatCount: boosterCount,
                },
                isQuotation: true,
            };
            const message = {
                event: RentalServiceEvent.BABY_SEAT_QUOTATION_REQUEST,
                data: JSON.stringify({
                    ...quotationPayload,
                    sessionUserId: getCookieShopify(),
                }),
                isCache: false,
                ip: '',
            };
            eventBusSocket.send(message);
            // Backend now associates this session with these counts.
            lastConfirmedPayloadRef.current = {
                infantSeatCount: infantCount,
                childSeatCount: childCount,
                boosterSeatCount: boosterCount,
            };
        }
        catch (_a) {
            // If WebSocket is unavailable, stop loading
            setIsQuoting(false);
            setIsPendingQuotation(false);
            setLoadingSeatTypes(new Set());
        }
    }, [offerId, transactionId, idSession]);
    return {
        isPendingQuotation,
        isQuoting,
        isRateTypeAnimating,
        quotedOptions,
        quotedExtraChargeDetail,
        loadingSeatTypes,
        requestQuotation,
        requestAvailabilityRefresh,
        resetQuotation,
        markAsQuoting,
    };
}
